import type { Sender } from './flex/types';
import {
  maskNGWords,
  removeInvisibleCharacters,
  segmentString,
} from 'src/replicant/utils/string';

/**
 * Maximum length of `sender.name` accepted by the LINE Messaging API.
 *
 * LINE measures the limit in UTF-16 code units, not visible characters:
 * a name that displays as 14 characters can exceed 20 units through
 * combining marks, surrogate pairs or emoji, and passes the LINE app's
 * own profile-name limit while still being rejected by the API with
 * `400 sender.name length is invalid`.
 */
const MAX_SENDER_NAME_LENGTH = 20;

/**
 * Invisible filler/blank characters the LINE API rejects with
 * `400 sender.name contains invalid character`: combining grapheme joiner
 * (U+034F), Hangul fillers (U+115F, U+1160, U+3164, U+FFA0 — the latter
 * two NFKC-fold into U+1160), braille blank (U+2800).
 */
const INVALID_SENDER_NAME_CHARACTERS =
  /[\u034F\u115F\u1160\u2800\u3164\uFFA0]/g;

/**
 * All Unicode format characters (category Cf): bidi controls such as the
 * Arabic letter mark (U+061C), word joiners, interlinear annotations and
 * the astral tag block (U+E0001+). Invisible, so they can both trip the
 * LINE API and smuggle blocked words past the NG-word mask.
 */
const FORMAT_CHARACTERS = /\p{Cf}/gu;

// Unpaired UTF-16 surrogates are invalid in any payload sent to LINE.
// Built via RegExp with lookahead only — lookbehind would throw at module
// load on older webviews. The second pattern re-inserts valid pairs via
// $1 and drops the lone low surrogates that match the bare alternative.
const HIGH_SURROGATE = '[\\uD800-\\uDBFF]';
const LOW_SURROGATE = '[\\uDC00-\\uDFFF]';
const LONE_HIGH_SURROGATES = new RegExp(
  `${HIGH_SURROGATE}(?!${LOW_SURROGATE})`,
  'g',
);
const SURROGATE_PAIRS_OR_LONE_LOW = new RegExp(
  `(${HIGH_SURROGATE}${LOW_SURROGATE})|${LOW_SURROGATE}`,
  'g',
);

/**
 * Make a user-provided name (player display name, pet name) safe to use
 * as a LINE chatbot `sender.name`. Returns an empty string when nothing
 * safe remains — callers should omit the name in that case so the
 * official account's default name is shown instead.
 */
export function sanitizeSenderName(name: string): string {
  // Fold stylistic Unicode the LINE API rejects (mathematical alphanumeric
  // letters, fullwidth forms, exotic spaces, Hangul filler U+3164) into
  // plain equivalents.
  let result = name.normalize('NFKC');

  result = removeInvisibleCharacters(result, '')
    .replace(FORMAT_CHARACTERS, '')
    .replace(INVALID_SENDER_NAME_CHARACTERS, '')
    .replace(LONE_HIGH_SURROGATES, '')
    .replace(SURROGATE_PAIRS_OR_LONE_LOW, '$1');

  // Recompose sequences the removals may have left decomposed so the
  // length budget is not wasted (e.g. "a" + stripped joiner + combining
  // acute recomposes to a single-unit "á").
  result = result.normalize('NFC');

  // Collapse all remaining whitespace (incl. ideographic space) to single
  // ASCII spaces.
  result = result.replace(/\s+/gu, ' ').trim();

  // maskNGWords only masks the first occurrence of each word variant per
  // pass, so repeat until stable.
  let previous: string;
  do {
    previous = result;
    result = maskNGWords(result);
  } while (result !== previous);

  if (result.length <= MAX_SENDER_NAME_LENGTH) return result;

  // Truncate at grapheme-cluster boundaries: cutting inside a cluster
  // could leave a lone surrogate or combining mark, which the API rejects
  // as an invalid character.
  let truncated = '';
  for (const grapheme of segmentString(result)) {
    if (truncated.length + grapheme.length > MAX_SENDER_NAME_LENGTH) break;
    truncated += grapheme;
  }
  return truncated.trim();
}

/**
 * Build the optional `sender` field of a LINE chatbot message from
 * unsanitized inputs. Omits the name when sanitization leaves nothing,
 * and the whole object when there is neither a name nor an icon.
 */
export function buildSender(sender: {
  name?: string;
  iconUrl?: string;
}): Sender | undefined {
  const sanitizedName = sender.name ? sanitizeSenderName(sender.name) : '';
  if (!sanitizedName && !sender.iconUrl) return undefined;
  return {
    ...(sanitizedName ? { name: sanitizedName } : {}),
    ...(sender.iconUrl ? { iconUrl: sender.iconUrl } : {}),
  };
}
